import type { FC, ReactNode } from 'react'
import type { GetStaticProps } from 'next'
import type { HomeData } from '@/lib/directory'

import Link from 'next/link'
import { ArrowRight, Search } from 'lucide-react'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { DirectoryCard } from '@/components/directory/directory-card'
import { RankedToolRow } from '@/components/directory/ranked-tool-row'
import { Badge } from '@/components/directory/tool-badges'
import { ToolLogo } from '@/components/directory/tool-logo'
import { SuggestToolForm } from '@/components/directory/suggest-tool-form'
import { iconFor } from '@/components/directory/category-icon'
import { getHomeData } from '@/lib/directory'

interface HomeProps {
    data: HomeData | null
}

const criteria = [
    {
        title: 'Output quality',
        body: 'Realism, motion, detail and how often a result is usable on the first try.'
    },
    {
        title: 'Control',
        body: 'Camera moves, references, editing and how precisely the tool follows a brief.'
    },
    {
        title: 'Value',
        body: 'What you get before paying, and how far a paid plan actually goes.'
    },
    {
        title: 'Reliability',
        body: 'Queue times, uptime, clear rights for commercial use and a track record of shipping.'
    }
]

const numberedRow = 'group relative flex items-center gap-3 rounded-xl px-2 py-2 transition-colors hover:bg-secondary/70'
const rowLink = 'min-w-0 flex-1 truncate text-sm font-semibold text-foreground after:absolute after:inset-0 focus-visible:outline-none focus-visible:after:rounded-xl focus-visible:after:outline-2 focus-visible:after:outline-primary'

const Home: FC<HomeProps> = ({ data }): ReactNode => {
    const stats = data?.stats

    const jumpLinks = [
        ...(data?.categories ?? []).map(category => ({
            key: category.id,
            href: `#cat-${category.slug}`,
            label: <span data-mdlfy-cms={`categories:${category.id}:name`}>{category.name}</span>
        })),
        { key: 'free-tools', href: '#free-tools', label: 'Free AI Tools' },
        { key: 'open-source', href: '#open-source', label: 'Open Source & Local' },
        { key: 'shut-down', href: '#shut-down', label: 'Shut Down' },
        { key: 'matchups', href: '#matchups', label: 'Matchups' },
        { key: 'use-cases', href: '#use-cases', label: 'By Use Case' }
    ]

    return (
        <Layout>
            <Seo />

            <section id='hero' className='relative overflow-hidden border-b border-border/70'>
                <div className='bg-grid pointer-events-none absolute inset-0' aria-hidden='true' />
                <div className='hero-glow pointer-events-none absolute inset-0' aria-hidden='true' />

                <div className='relative mx-auto max-w-5xl px-4 pb-16 pt-16 text-center sm:px-6 sm:pt-24 lg:px-8'>
                    <p className='animate-in fade-in slide-in-from-bottom-2 inline-flex items-center gap-2 rounded-full border border-border bg-card/70 px-3.5 py-1.5 text-xs font-medium text-muted-foreground duration-500'>
                        <span className='relative flex size-2'>
                            <span className='absolute inline-flex size-full animate-ping rounded-full bg-primary opacity-60 motion-reduce:animate-none' />
                            <span className='relative inline-flex size-2 rounded-full bg-primary' />
                        </span>
                        {stats?.updated ? `Rankings updated ${stats.updated}` : 'Independent rankings'}
                    </p>

                    <h1 className='animate-in fade-in slide-in-from-bottom-3 mt-6 font-heading text-5xl font-bold leading-[0.98] tracking-tight text-foreground duration-700 sm:text-6xl lg:text-7xl'>
                        Every AI video and image tool, <span className='text-primary'>ranked.</span>
                    </h1>

                    <p className='animate-in fade-in slide-in-from-bottom-3 mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-muted-foreground duration-700'>
                        Editorial scores, real free tiers, open-source picks, head-to-head matchups and a tracker for the tools that shut down. Every category, one tap away.
                    </p>

                    <form
                        action='/tools'
                        method='get'
                        role='search'
                        className='animate-in fade-in slide-in-from-bottom-4 mx-auto mt-9 flex max-w-xl items-center gap-2 rounded-full border border-border bg-card/90 p-1.5 pl-5 shadow-[0_20px_60px_-25px_rgb(0_0_0/0.8)] duration-700 focus-within:border-primary/60'
                    >
                        <Search className='size-5 shrink-0 text-muted-foreground' aria-hidden='true' />

                        <label htmlFor='hero-search' className='sr-only'>
                            Search AI tools
                        </label>

                        <input
                            id='hero-search'
                            name='q'
                            type='search'
                            placeholder='Search Kling, Midjourney, upscalers...'
                            className='min-w-0 flex-1 bg-transparent py-2 text-base text-foreground placeholder:text-muted-foreground focus:outline-none'
                        />

                        <button
                            type='submit'
                            className='shrink-0 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition-transform hover:-translate-y-px focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary'
                        >
                            Search
                        </button>
                    </form>

                    {stats && (
                        <dl className='mx-auto mt-10 grid max-w-lg grid-cols-3 divide-x divide-border/70 rounded-2xl border border-border bg-card/60'>
                            {[
                                { label: 'Tools ranked', value: stats.tools },
                                { label: 'Categories', value: stats.categories },
                                { label: 'Matchups', value: stats.comparisons }
                            ].map(stat => (
                                <div key={stat.label} className='px-3 py-4'>
                                    <dt className='text-xs text-muted-foreground'>{stat.label}</dt>
                                    <dd className='mt-1 font-mono text-2xl font-semibold tabular-nums text-foreground'>{stat.value}</dd>
                                </div>
                            ))}
                        </dl>
                    )}
                </div>
            </section>

            <section id='categories' aria-labelledby='categories-heading' className='mx-auto max-w-7xl scroll-mt-20 px-4 pt-14 sm:px-6 lg:px-8'>
                <div className='flex flex-wrap items-end justify-between gap-4'>
                    <div>
                        <h2 id='categories-heading' className='font-heading text-3xl font-bold tracking-tight text-foreground sm:text-4xl'>
                            Every category, one tap away
                        </h2>
                        <p className='mt-2 text-muted-foreground'>
                            The top five in each, ranked by score. Tap a category for the full list.
                        </p>
                    </div>

                    <Link
                        href='/tools'
                        className='inline-flex items-center gap-1.5 rounded-full border border-border px-4 py-2 text-sm font-semibold text-foreground transition-colors hover:border-primary hover:text-primary focus-visible:outline-2 focus-visible:outline-primary'
                    >
                        Full directory
                        <ArrowRight className='size-4' aria-hidden='true' />
                    </Link>
                </div>

                <nav aria-label='Jump to a category' className='no-scrollbar -mx-4 mt-6 overflow-x-auto px-4'>
                    <ul className='flex w-max gap-2 pb-1'>
                        {jumpLinks.map(link => (
                            <li key={link.key}>
                                <a
                                    href={link.href}
                                    className='inline-flex items-center whitespace-nowrap rounded-full border border-border bg-card px-3.5 py-1.5 text-sm font-medium text-muted-foreground transition-colors hover:border-primary/50 hover:text-foreground focus-visible:outline-2 focus-visible:outline-primary'
                                >
                                    {link.label}
                                </a>
                            </li>
                        ))}
                    </ul>
                </nav>

                {!data && (
                    <p className='mt-10 rounded-2xl border border-dashed border-border px-6 py-14 text-center text-muted-foreground'>
                        The rankings are taking a moment to load. Please check back shortly.
                    </p>
                )}

                {data && (
                    <div className='mt-8 grid gap-5 md:grid-cols-2 xl:grid-cols-3'>
                        {data.categories.map(category => (
                            <DirectoryCard
                                key={category.id}
                                id={`cat-${category.slug}`}
                                href={`/category/${category.slug}`}
                                icon={iconFor(category.slug)}
                                title={<span data-mdlfy-cms={`categories:${category.id}:name`}>{category.name}</span>}
                                description={category.tagline && <span data-mdlfy-cms={`categories:${category.id}:tagline`}>{category.tagline}</span>}
                                footerLabel={`See all ${category.total}`}
                                isEmpty={category.tools.length === 0}
                            >
                                <ol className='space-y-0.5'>
                                    {category.tools.map((tool, index) => (
                                        <RankedToolRow key={tool.id} tool={tool} rank={index + 1} />
                                    ))}
                                </ol>
                            </DirectoryCard>
                        ))}

                        <DirectoryCard
                            id='free-tools'
                            href='/free-tools'
                            icon={iconFor('free-tools')}
                            title='Free AI Tools'
                            description='Real free tiers you can use before a card ever comes out.'
                            footerLabel={`See all ${data.free.total}`}
                            isEmpty={data.free.tools.length === 0}
                        >
                            <ol className='space-y-0.5'>
                                {data.free.tools.map((tool, index) => (
                                    <RankedToolRow key={tool.id} tool={tool} rank={index + 1} />
                                ))}
                            </ol>
                        </DirectoryCard>

                        <DirectoryCard
                            id='open-source'
                            href='/open-source'
                            icon={iconFor('open-source')}
                            title='Open Source & Local'
                            description='Models and apps you can download and run on your own machine.'
                            footerLabel={`See all ${data.openSource.total}`}
                            isEmpty={data.openSource.tools.length === 0}
                        >
                            <ol className='space-y-0.5'>
                                {data.openSource.tools.map((tool, index) => (
                                    <RankedToolRow key={tool.id} tool={tool} rank={index + 1} />
                                ))}
                            </ol>
                        </DirectoryCard>

                        <DirectoryCard
                            id='shut-down'
                            href='/shut-down'
                            icon={iconFor('shut-down')}
                            accent='muted'
                            title='Shut Down: What Happened'
                            description='Dead tools people still search for, and what replaced them.'
                            footerLabel={`See all ${data.shutDown.total}`}
                            isEmpty={data.shutDown.tools.length === 0}
                        >
                            <ol className='space-y-0.5'>
                                {data.shutDown.tools.map((tool, index) => (
                                    <li key={tool.id} className={numberedRow}>
                                        <span className='w-5 shrink-0 text-center font-mono text-xs tabular-nums text-muted-foreground'>
                                            {index + 1}
                                        </span>

                                        <ToolLogo tool={tool} size='sm' className='opacity-60 grayscale' />

                                        <Link href={`/tools/${tool.slug}`} className={rowLink}>
                                            <span data-mdlfy-cms={`tools:${tool.id}:name`}>{tool.name}</span>
                                        </Link>

                                        <Badge tone='dead'>dead</Badge>

                                        {tool.ended_label && (
                                            <span className='hidden w-20 shrink-0 text-right font-mono text-[11px] text-muted-foreground sm:inline'>
                                                {tool.ended_label}
                                            </span>
                                        )}
                                    </li>
                                ))}
                            </ol>
                        </DirectoryCard>

                        <DirectoryCard
                            id='matchups'
                            href='/compare'
                            icon={iconFor('matchups')}
                            title='Matchups & Alternatives'
                            description='Side-by-side verdicts and what to use instead.'
                            footerLabel='All comparisons'
                            isEmpty={data.comparisons.items.length === 0 && data.alternatives.length === 0}
                        >
                            <ol className='space-y-0.5'>
                                {data.comparisons.items.map((comparison, index) => (
                                    <li key={comparison.id} className={numberedRow}>
                                        <span className='w-5 shrink-0 text-center font-mono text-xs tabular-nums text-muted-foreground'>
                                            {index + 1}
                                        </span>

                                        <Link href={`/compare/${comparison.slug}`} className={rowLink}>
                                            <span data-mdlfy-cms={`tools:${comparison.tool_a.id}:name`}>{comparison.tool_a.name}</span>
                                            <span className='text-muted-foreground'> vs </span>
                                            <span data-mdlfy-cms={`tools:${comparison.tool_b.id}:name`}>{comparison.tool_b.name}</span>
                                        </Link>
                                    </li>
                                ))}

                                {data.alternatives.map((tool, index) => (
                                    <li key={tool.id} className={numberedRow}>
                                        <span className='w-5 shrink-0 text-center font-mono text-xs tabular-nums text-muted-foreground'>
                                            {data.comparisons.items.length + index + 1}
                                        </span>

                                        <Link href={`/alternatives/${tool.slug}`} className={rowLink}>
                                            <span data-mdlfy-cms={`tools:${tool.id}:name`}>{tool.name}</span>
                                            <span className='text-muted-foreground'> alternatives</span>
                                        </Link>
                                    </li>
                                ))}
                            </ol>
                        </DirectoryCard>

                        <DirectoryCard
                            id='use-cases'
                            href='/use-cases'
                            icon={iconFor('use-cases')}
                            title='AI Tools by Use Case'
                            description='Product shots, thumbnails, music videos and more, ranked.'
                            footerLabel={`All ${data.useCases.total} use cases`}
                            isEmpty={data.useCases.items.length === 0}
                        >
                            <ol className='space-y-0.5'>
                                {data.useCases.items.map((useCase, index) => (
                                    <li key={useCase.id} className={numberedRow}>
                                        <span className='w-5 shrink-0 text-center font-mono text-xs tabular-nums text-muted-foreground'>
                                            {index + 1}
                                        </span>

                                        <Link href={`/use-cases/${useCase.slug}`} className={rowLink}>
                                            <span data-mdlfy-cms={`use_cases:${useCase.id}:name`}>{useCase.name}</span>
                                        </Link>

                                        <ArrowRight className='size-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-primary' aria-hidden='true' />
                                    </li>
                                ))}
                            </ol>
                        </DirectoryCard>
                    </div>
                )}
            </section>

            <section id='how-we-rank' aria-labelledby='how-we-rank-heading' className='mx-auto mt-24 max-w-7xl px-4 sm:px-6 lg:px-8'>
                <div className='grid gap-10 rounded-3xl border border-border bg-card/60 p-6 sm:p-10 lg:grid-cols-[1fr_1.6fr] lg:gap-14'>
                    <div>
                        <p className='font-mono text-xs font-medium uppercase tracking-[0.18em] text-primary'>
                            Methodology
                        </p>
                        <h2 id='how-we-rank-heading' className='mt-3 font-heading text-3xl font-bold tracking-tight text-foreground sm:text-4xl'>
                            How we rank
                        </h2>
                        <p className='mt-4 leading-relaxed text-muted-foreground'>
                            Every score runs from 0 to 10 and is an editorial judgment across four things that matter when you are choosing a tool. Tools without a score yet are listed after the scored ones, A to Z.
                        </p>
                    </div>

                    <ul className='grid gap-4 sm:grid-cols-2'>
                        {criteria.map((item, index) => (
                            <li key={item.title} className='rounded-2xl border border-border bg-background/60 p-5'>
                                <span className='font-mono text-xs text-primary'>
                                    0{index + 1}
                                </span>
                                <h3 className='mt-2 font-heading text-lg font-semibold text-foreground'>
                                    {item.title}
                                </h3>
                                <p className='mt-1.5 text-sm leading-relaxed text-muted-foreground'>
                                    {item.body}
                                </p>
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            <section id='suggest' aria-labelledby='suggest-heading' className='mx-auto mt-24 max-w-7xl px-4 sm:px-6 lg:px-8'>
                <div className='grid gap-10 lg:grid-cols-[1fr_1.2fr] lg:items-start'>
                    <div>
                        <h2 id='suggest-heading' className='font-heading text-3xl font-bold tracking-tight text-foreground sm:text-4xl'>
                            Know a tool we missed?
                        </h2>
                        <p className='mt-4 max-w-md leading-relaxed text-muted-foreground'>
                            New video and image tools launch every week. Send us the ones worth a look, or tell us when a listing is out of date, and we will review it for the next update.
                        </p>
                    </div>

                    <SuggestToolForm />
                </div>
            </section>
        </Layout>
    )
}

export const getStaticProps: GetStaticProps<HomeProps> = async () => {
    try {
        const data = await getHomeData()

        return { props: { data }, revalidate: 60 }
    }
    catch (error) {
        console.error('Home page data failed to load:', error instanceof Error ? error.message : error)

        return { props: { data: null }, revalidate: 30 }
    }
}

export default Home
